import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Paths } from "@/paths";
import {
    Breadcrumb, Button, Card, EmptyState, ErrorNote, LaunchStatusBadge, PageHeader, ReadOnlyTag, SkeletonLines, Tabs, ds,
} from "@/shared/components/ds";
import { OpLaunchPage } from "../OpLaunchPage";
import { show } from "../format";
import type { LaunchDetail } from "../models/launchDetail";
import { CommandGetLaunchDetail } from "../services/cmdGetLaunchDetail";
import pageStyles from "../opLaunchPages.module.css";
import styles from "./launchDetail.module.css";
import { TABS } from "./tabs";

/**
 * One OP launch, read-only, as widget cards under sticky tabs (MPN-2229). The tab lives in
 * ?tab= so a link lands on it. The breadcrumb, the header and the tabs are one block that
 * stays put while the tab's content scrolls under it (MPN-2313).
 */
export default function OpLaunchDetailPage() {
    const { launchId = "" } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [detail, setDetail] = useState<LaunchDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const tab = TABS.find((t) => t.key === searchParams.get("tab")) ?? TABS[0];

    useEffect(() => {
        let live = true;
        setDetail(null);
        setError(null);
        setLoading(true);
        new CommandGetLaunchDetail().run(launchId)
            .then((result) => { if (live) setDetail(result); })
            .catch((e) => { if (live) setError(e instanceof Error ? e.message : "Could not load the launch."); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [launchId]);

    const main = detail?.main;
    const notFound = !loading && !error && !main;
    const dashboard = `/${Paths.OP_LAUNCH_DASHBOARD}?launch_id=${launchId}`;

    return (
        <OpLaunchPage title={`OP Launch ${launchId}`}>
            <div className={pageStyles.stickyHeader}>
                <Breadcrumb items={[{ label: "Admin" }, { label: "Old Platform" }, { label: "OP Launches", to: `/${Paths.OP_LAUNCHES}` }, { label: launchId }]} />
                <PageHeader title={main ? `${launchId}: ${show(main.name)}` : "Launch Details"}
                    actions={main && <Button variant="secondary" size="sm" icon={<i className="bi bi-speedometer2" aria-hidden="true" />} onClick={() => navigate(dashboard)}>View Dashboard</Button>}>
                    {main && (
                        <>
                            <span className={styles.headerMeta}><i className="bi bi-building" aria-hidden="true" />{show(main.affiliate_name)}</span>
                            <LaunchStatusBadge active={main.is_active} />
                        </>
                    )}
                    <ReadOnlyTag />
                </PageHeader>
                {detail && main && (
                    <div className={pageStyles.stickyInner}>
                        <Tabs bare tabs={TABS.map((t) => ({ value: t.key, label: t.label }))} value={tab.key} onChange={(key) => setSearchParams({ tab: key })} />
                    </div>
                )}
            </div>
            {error && <ErrorNote>{error}</ErrorNote>}
            {loading && <Card><SkeletonLines /></Card>}
            {notFound && (
                <Card>
                    <div className={styles.notFound}>
                        <EmptyState title="Launch not found" body="Pick a launch ID from OP Launches to see its details."
                            action={<Link to={`/${Paths.OP_LAUNCHES}`} className={ds.link}>Browse OP Launches</Link>} />
                    </div>
                </Card>
            )}
            {detail && main && tab.render(detail)}
        </OpLaunchPage>
    );
}
